import React, { useState, useEffect } from"react";
import { useTranslation } from"react-i18next";
import {
 DollarSign,
 CheckCircle,
 Clock,
 Filter,
 Eye,
 ArrowLeft,
} from"lucide-react";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Toast,
 ToastType,
 ModernButton,
 ModernInput,
 ModernSelect,
 ListPageSkeleton,
} from"../../components/ui";
import { DatePicker } from"../../components/form/DatePicker";
import { apiService } from"../../services/api";
import { Commission } from"../../types";
import { formatCurrency, formatDateToBR } from"../../utils/formatters";
import { resolveCommissionDisplay } from"../../utils/commissionCalculation";
import { useAuth } from"../../contexts/AuthContext";

export const CommissionsPage: React.FC = () => {
 const { t } = useTranslation();
 const { user } = useAuth();
 const [commissions, setCommissions] = useState<Commission[]>([]);
 const [isLoading, setIsLoading] = useState(true);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);
 
 // Check if user can mark commissions as paid (owner, manager, or financial)
 const canMarkAsPaid = user?.dealershipRole === 'owner' || user?.dealershipRole === 'manager' || user?.dealershipRole === 'financial';
 // Helper function to get first day of current month
 const getFirstDayOfMonth = () => {
 const now = new Date();
 return new Date(now.getFullYear(), now.getMonth(), 1)
 .toISOString()
 .split("T")[0];
 };

 // Helper function to get today's date
 const getToday = () => {
 return new Date().toISOString().split("T")[0];
 };

 const [filterStatus, setFilterStatus] = useState<"all"|"pending"|"paid">(
"all"
 );
 const [salespersonFilter, setSalespersonFilter] = useState<string>("");
 const [startDateFilter, setStartDateFilter] = useState<string>(
 getFirstDayOfMonth()
 );
 const [endDateFilter, setEndDateFilter] = useState<string>(getToday());
 const [selectedCommission, setSelectedCommission] =
 useState<Commission | null>(null);
 const [showDetails, setShowDetails] = useState(false);

 useEffect(() => {
 fetchCommissions();
 }, [page, filterStatus, salespersonFilter, startDateFilter, endDateFilter]);

 const fetchCommissions = async () => {
 try {
 setIsLoading(true);
 let response;
 if (salespersonFilter) {
 response = await apiService.getCommissionsBySalesperson(
 salespersonFilter,
 {
 page,
 limit: 50,
 }
 );
 } else {
 response = await apiService.getCommissions({ page, limit: 50 });
 }

 let filtered = response.commissions || [];
 if (filterStatus !=="all") {
 filtered = filtered.filter(
 (c: Commission) => c.status === filterStatus
 );
 }
 if (startDateFilter) {
 filtered = filtered.filter(
 (c: Commission) => new Date(c.createdAt) >= new Date(startDateFilter)
 );
 }
 if (endDateFilter) {
 filtered = filtered.filter(
 (c: Commission) => new Date(c.createdAt) <= new Date(endDateFilter)
 );
 }

 setCommissions(filtered);
 setTotalPages(response.pagination?.totalPages || 1);
 } catch (err) {
 console.error("Erro ao carregar comissões:", err);
 setToast({
 message:"Erro ao carregar comissões",
 type:"error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const getStatusIcon = (status: Commission["status"]) => {
 switch (status) {
 case"paid":
 return <CheckCircle className="h-5 w-5 text-green-600"/>;
 case"pending":
 return <Clock className="h-5 w-5 text-amber-600"/>;
 default:
 return <Clock className="h-5 w-5 text-muted-foreground"/>;
 }
 };

 const getStatusLabel = (status: Commission["status"]) => {
 const labels: Record<Commission["status"], string> = {
 pending:"Pendente",
 paid:"Paga",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };

 const handleViewDetails = async (commissionId: string) => {
 try {
 const commission = await apiService.getCommissionById(commissionId);
 setSelectedCommission(commission);
 setShowDetails(true);
 } catch (err) {
 console.error("Erro ao carregar detalhes da comissão:", err);
 setToast({
 message:"Erro ao carregar detalhes da comissão",
 type:"error",
 });
 }
 };

 const handleCloseDetails = () => {
 setShowDetails(false);
 setSelectedCommission(null);
 };

 const handleMarkAsPaid = async (commissionId: string) => {
 const row = commissions.find((c) => c.id === commissionId);
 if (!row?.sale?.commissionsConfirmedAt) {
 setToast({
 message: t("financialSales.commissions.payRequiresCommissionConfirmation"),
 type: "error",
 });
 return;
 }
 try {
 await apiService.markCommissionAsPaid(commissionId, '');
 setToast({
 message: 'Comissão marcada como paga com sucesso',
 type: 'success',
 });
 // Refresh commissions list
 fetchCommissions();
 } catch (err: any) {
 console.error('Erro ao marcar comissão como paga:', err);
 setToast({
 message: err.message || 'Erro ao marcar comissão como paga',
 type: 'error',
 });
 }
 };

 const totalPending = commissions
 .filter((c) => c.status ==="pending")
 .reduce((sum, c) => sum + parseFloat(c.calculatedValue.toString()), 0);

 const totalPaid = commissions
 .filter((c) => c.status ==="paid")
 .reduce((sum, c) => sum + parseFloat(c.calculatedValue.toString()), 0);

 // ── DETAIL VIEW ─────────────────────────────────────────────────────────
 if (showDetails && selectedCommission) {
 return (
 <div className="space-y-6">
 <div className="flex items-center gap-4">
 <ModernButton variant="outline" size="sm" onClick={handleCloseDetails}>
 <ArrowLeft className="h-4 w-4 mr-1"/>
 Voltar
 </ModernButton>
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">Detalhes da Comissão</h1>
 <p className="text-sm text-muted-foreground mt-0.5">
 {selectedCommission.salesperson?.name ||"Vendedor"} — {getStatusLabel(selectedCommission.status)}
 </p>
 </div>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <Card>
 <CardHeader><CardTitle className="text-base">Informações Gerais</CardTitle></CardHeader>
 <CardContent className="space-y-3">
 <div className="flex justify-between"><span className="text-sm text-muted-foreground">Vendedor:</span><span className="font-medium">{selectedCommission.salesperson?.name ||"N/A"}</span></div>
 <div className="flex justify-between"><span className="text-sm text-muted-foreground">Status:</span><div className="flex items-center gap-2">{getStatusIcon(selectedCommission.status)}<span>{getStatusLabel(selectedCommission.status)}</span></div></div>
 <div className="flex justify-between"><span className="text-sm text-muted-foreground">Valor Base:</span><span className="font-medium">{formatCurrency(parseFloat(selectedCommission.baseValue.toString()))}</span></div>
 <div className="flex justify-between"><span className="text-sm text-muted-foreground">Percentual:</span><span className="font-medium">{parseFloat(selectedCommission.percentage.toString())}%</span></div>
 <div className="flex justify-between pt-2 border-t border-border"><span className="font-semibold">Valor Calculado:</span><span className="font-bold text-green-600">{formatCurrency(parseFloat(selectedCommission.calculatedValue.toString()))}</span></div>
 </CardContent>
 </Card>

 {selectedCommission.rule && (
 <Card>
 <CardHeader><CardTitle className="text-base">Regra Aplicada</CardTitle></CardHeader>
 <CardContent className="space-y-2">
 <div><span className="text-sm text-muted-foreground">Nome:</span><p className="font-medium">{selectedCommission.rule.name}</p></div>
 {selectedCommission.rule.description && <div><span className="text-sm text-muted-foreground">Descrição:</span><p className="text-sm">{selectedCommission.rule.description}</p></div>}
 <div><span className="text-sm text-muted-foreground">Tipo:</span><p className="font-medium">{selectedCommission.rule.type ==="percentage"?"Percentual Fixo":"Por Faixas"}</p></div>
 {selectedCommission.rule.percentageValue && <div><span className="text-sm text-muted-foreground">Percentual:</span><p className="font-medium">{selectedCommission.rule.percentageValue}%</p></div>}
 </CardContent>
 </Card>
 )}

 {selectedCommission.sale && (
 <Card>
 <CardHeader><CardTitle className="text-base">Venda Relacionada</CardTitle></CardHeader>
 <CardContent className="space-y-2">
 <div><span className="text-sm text-muted-foreground">Data:</span><p className="font-medium">{formatDateToBR(selectedCommission.sale.saleDate)}</p></div>
 {selectedCommission.sale.vehicle && <div><span className="text-sm text-muted-foreground">Veículo:</span><p className="font-medium">{selectedCommission.sale.vehicle.brand?.name ||""} {selectedCommission.sale.vehicle.model?.name ||""} {selectedCommission.sale.vehicle.modelYear ||""}</p></div>}
 <div><span className="text-sm text-muted-foreground">Valor Final:</span><p className="font-medium">{formatCurrency(parseFloat(selectedCommission.sale.finalValue.toString()))}</p></div>
 </CardContent>
 </Card>
 )}

 {selectedCommission.paidAt && (
 <Card>
 <CardHeader><CardTitle className="text-base">Histórico de Pagamento</CardTitle></CardHeader>
 <CardContent className="space-y-2">
 <div><span className="text-sm text-muted-foreground">Data do Pagamento:</span><p className="font-medium">{formatDateToBR(selectedCommission.paidAt)}</p></div>
 {selectedCommission.payment && <div><span className="text-sm text-muted-foreground">Pagamento:</span><p className="font-medium">{formatCurrency(parseFloat(selectedCommission.payment.amount.toString()))} - {selectedCommission.payment.paymentMethod}</p></div>}
 </CardContent>
 </Card>
 )}
 </div>
 {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />}
 </div>
 );
 }

 // ── LIST VIEW ─────────────────────────────────────────────────────────────
 return (
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <h1 className="text-2xl font-bold text-foreground font-display">
 Comissões
 </h1>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">
 Total Pendente
 </p>
 <p className="text-2xl font-bold text-foreground font-display">
 {formatCurrency(totalPending)}
 </p>
 </div>
 <Clock className="h-8 w-8 text-amber-600"/>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">
 Total Pago
 </p>
 <p className="text-2xl font-bold text-foreground font-display">
 {formatCurrency(totalPaid)}
 </p>
 </div>
 <CheckCircle className="h-8 w-8 text-green-600"/>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">
 Total Geral
 </p>
 <p className="text-2xl font-bold text-foreground font-display">
 {formatCurrency(totalPending + totalPaid)}
 </p>
 </div>
 <DollarSign className="h-8 w-8 text-blue-600"/>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Filters */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Filter className="h-5 w-5"/>
 Filtros
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <ModernSelect
 label="Status"
 value={filterStatus}
 onChange={(e) => setFilterStatus(e.target.value as "all" | "pending" | "paid")}
 options={[
 { value: "all", label: "Todos" },
 { value: "pending", label: "Pendentes" },
 { value: "paid", label: "Pagas" },
 ]}
 placeholder=""
 />
 <ModernInput
 label="Vendedor (ID)"
 type="text"
 value={salespersonFilter}
 onChange={(e) => setSalespersonFilter(e.target.value)}
 placeholder="Filtrar por ID do vendedor"
 />
 <div>
 <DatePicker
 label="Data Inicial"
 value={startDateFilter}
 onChange={(value) => setStartDateFilter(value)}
 placeholder="Selecione a data inicial"
 />
 </div>
 <div>
 <DatePicker
 label="Data Final"
 value={endDateFilter}
 onChange={(value) => setEndDateFilter(value)}
 placeholder="Selecione a data final"
 />
 </div>
 </div>
 <div className="mt-4">
 <ModernButton
 variant="outline"
 onClick={() => {
 setFilterStatus("all");
 setSalespersonFilter("");
 setStartDateFilter("");
 setEndDateFilter("");
 }}
 >
 Limpar Filtros
 </ModernButton>
 </div>
 </CardContent>
 </Card>

 {/* Commissions List */}
 <Card>
 <CardHeader>
 <CardTitle>Lista de Comissões</CardTitle>
 </CardHeader>
 <CardContent>
 {isLoading ? (
 <ListPageSkeleton variant="list" rows={6} showFilters={false} />
 ) : commissions.length === 0 ? (
 <p className="text-sm text-muted-foreground">
 Nenhuma comissão encontrada
 </p>
 ) : (
 <div className="space-y-3">
 {commissions.map((commission) => {
 const display = resolveCommissionDisplay(commission, commission.sale ?? undefined);

 return (
 <div
 key={commission.id}
 className="flex items-center justify-between p-4 border border-border rounded-lg"
 >
 <div className="flex items-center gap-4 flex-1">
 {getStatusIcon(commission.status)}
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <h3 className="font-semibold text-foreground">
 {commission.salesperson?.name ||"Vendedor"}
 </h3>
 <span
 className={`px-2 py-1 text-xs rounded-full ${
 commission.status ==="paid"
 ?"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
 :"bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
 }`}
 >
 {getStatusLabel(commission.status)}
 </span>
 </div>
 <div className="mt-1 text-sm text-muted-foreground">
 <div className="flex items-center gap-4">
 {commission.sale && (
 <span>
 Venda: {commission.sale.vehicle?.brand?.name}{""}
 {commission.sale.vehicle?.model?.name}
 </span>
 )}
 <span>
 Base: {formatCurrency(display.baseValue)} | {display.percentage.toFixed(2)}%
 </span>
 {commission.rule && (
 <span>Regra: {commission.rule.name}</span>
 )}
 </div>
 </div>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <div className="text-right">
 <div className="font-semibold text-foreground">
 {formatCurrency(display.calculatedValue)}
 </div>
 {commission.paidAt && (
 <div className="text-sm text-muted-foreground">
 Pago em: {formatDateToBR(commission.paidAt)}
 </div>
 )}
 </div>
 {canMarkAsPaid &&
 commission.status === "pending" &&
 Boolean(commission.sale?.commissionsConfirmedAt) && (
 <ModernButton
 size="sm"
 onClick={() => handleMarkAsPaid(commission.id)}
 className="bg-green-600 hover:bg-green-700 text-white border-0 focus:ring-green-500"
 >
 <CheckCircle className="h-4 w-4 mr-1"/>
 Marcar como Paga
 </ModernButton>
 )}
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => handleViewDetails(commission.id)}
 className="px-2"
 aria-label="Ver detalhes"
 >
 <Eye className="h-4 w-4"/>
 </ModernButton>
 </div>
 </div>
 );
 })}
 </div>
 )}

 {totalPages > 1 && (
 <div className="flex items-center justify-center gap-2 mt-4">
 <ModernButton
 variant="outline"
 onClick={() => setPage((p) => Math.max(1, p - 1))}
 disabled={page === 1}
 >
 Anterior
 </ModernButton>
 <span className="text-sm text-muted-foreground">
 Página {page} de {totalPages}
 </span>
 <ModernButton
 variant="outline"
 onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
 disabled={page === totalPages}
 >
 Próxima
 </ModernButton>
 </div>
 )}
 </CardContent>
 </Card>

 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}
 </div>
 );
};
